import { useCallback } from "react";
import { useSuccess } from "@/components/SuccessModal";
import type { DisclosureHost } from "@/hooks/use-disclosure";
import { useReduceDisclosure } from "@/hooks/use-reduce-disclosure";
import { ADD_PATIENT_SOURCES, CREATE_ACTIONS } from "../nav-bar-constant";
import type { AddPatientSource, CreateAction } from "../nav-bar-types";

export interface NavBarCreateFlowsState {
  spotLight: DisclosureHost;
  addPatientSource: DisclosureHost;
  addPatient: DisclosureHost;
  uploadPatients: DisclosureHost;
  newAppointment: DisclosureHost;
  instantAppointment: DisclosureHost;
  ehrInviteSent: boolean;
  closeEhrInviteSent: () => void;
  startCreateAction: (action: CreateAction) => void;
  selectAddPatientSource: (source: AddPatientSource | null) => void;
}

/**
 * Owns every dialog the Create menu can open, so the bar itself stays layout.
 */
export const useNavBarCreateFlows = (): NavBarCreateFlowsState => {
  const [
    spotLight,
    addPatientSource,
    addPatient,
    uploadPatients,
    newAppointment,
    instantAppointment,
  ] = useReduceDisclosure(6);

  const [
    ehrInviteSent,
    { open: openEhrInviteSent, close: closeEhrInviteSent },
  ] = useSuccess();

  const startCreateAction = useCallback(
    (action: CreateAction) => {
      const starters: Record<CreateAction, () => void> = {
        [CREATE_ACTIONS.PATIENT]: addPatientSource.open,
        [CREATE_ACTIONS.APPOINTMENT]: newAppointment.open,
        [CREATE_ACTIONS.INSTANT_APPOINTMENT]: instantAppointment.open,
      };
      starters[action]();
    },
    [addPatientSource.open, newAppointment.open, instantAppointment.open]
  );

  const selectAddPatientSource = useCallback(
    (source: AddPatientSource | null) => {
      if (source === ADD_PATIENT_SOURCES.CSV) {
        uploadPatients.open();
        return;
      }
      if (source === ADD_PATIENT_SOURCES.EHR) {
        openEhrInviteSent();
        addPatientSource.close();
        return;
      }
      if (source === ADD_PATIENT_SOURCES.PATIENT) {
        addPatient.open();
        addPatientSource.close();
      }
    },
    [
      uploadPatients.open,
      openEhrInviteSent,
      addPatientSource.close,
      addPatient.open,
    ]
  );

  return {
    spotLight,
    addPatientSource,
    addPatient,
    uploadPatients,
    newAppointment,
    instantAppointment,
    ehrInviteSent,
    closeEhrInviteSent,
    startCreateAction,
    selectAddPatientSource,
  };
};
